import { gql } from '@apollo/client';

// List query omits totalCount, hasNextPage, and hasPreviousPage so the server can avoid
// that work on the hot path; cursors still enable pagination (NRReactGrid infers next/prev when booleans are absent).
export const DEPENDENCIES = gql`
  query dependencies(
    $args: DependencyInput!
    $includeIdentifiedVia: Boolean = false
    $includeConfidence: Boolean = false
    $includeAiRelatedDependencies: Boolean = false
    $includeAiModelAttributes: Boolean = false
    $includeReachabilityV2: Boolean = false
  ) {
    dependencies(args: $args) {
      pageInfo {
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          submitDatetime
          dependency {
            id
            identificationIds
            cpes
            purls
            name
            type
            subtype
            operatingSystem
            operatingSystemKernelVersion
            language
            namespace
            onDisk
            # isPredicted // TODO: Uncomment this when the isPredicted column is ready
            path
            vendor
            product
            license
            package {
              type
            }
            sbomDependenciesCount
            version {
              id
            }
            identifiers {
              uri
              type
            }
            digest {
              md5
              sha1
              sha256
            }
            description
            confidence @include(if: $includeConfidence)
          }
          analytic {
            vulnerability {
              critical
              high
              medium
              low
            }
            exploit {
              threatActor
              ransomware
              botnet
              inTheWild
              knownAttacks
              inKnownExploitedVulnerabilities
              exploitCode
            }
          }
          verification {
            heuristic
            cryptographic
            package
            symIndex
            functionHashing
            userModified
          }
          identifiedVia @include(if: $includeIdentifiedVia)
          scope {
            value
            description
            actualScope
          }
          isAi @include(if: $includeAiRelatedDependencies)
          aiModelAttributes @include(if: $includeAiModelAttributes) {
            modelCategory
            severity
            modelAttributes
          }
          aiProviders @include(if: $includeAiRelatedDependencies)
          aiArchitecture @include(if: $includeAiRelatedDependencies)
          correlationsCount
          latestRemediation {
            author
            reason
            remediationAction
            createdAt
            operationId
            operationType
            identificationId
          }
          isReachable @include(if: $includeReachabilityV2)
        }
      }
    }
  }
`;

export const GET_DEPENDENCY_REACHABILITY = gql`
  query getDependencyReachability($args: GetDependencyReachabilityInput!) {
    getDependencyReachability(args: $args) {
      user
      entryPoint
      entryType
      scripts {
        path
        invocation
        edgeType
        detail
      }
    }
  }
`;

// Count-only query for SBOM table pagination total.
export const DEPENDENCIES_TOTAL_COUNT = gql`
  query dependenciesTotalCount($args: DependencyInput!) {
    dependencies(args: $args) {
      pageInfo {
        totalCount
      }
    }
  }
`;

export const DEPENDENCY_KNOWN_EXPLOITS = gql`
  query dependencyKnownExploits($args: DependencyKnownExploitsInput!) {
    dependencyKnownExploits(args: $args) {
      exploits {
        url
        name
        refsource
        dateAdded
        exploitMaturity
        exploitAvailability
      }
      botnets {
        botnetName
        dateAdded
      }
      threatActors {
        name
        aliases
        url
      }
      ransomware {
        families
        references {
          url
          date
        }
      }
      knownAttacks
      knownExploitedVulnerabilities {
        action
        dateAdded
        dueDate
        name
      }
    }
  }
`;

export const GROUPED_DEPENDENCIES = gql`
  query groupedDependencies($args: GroupedDependenciesInput!) {
    groupedDependencies(args: $args) {
      pageInfo {
        hasPreviousPage
        hasNextPage
        startCursor
        endCursor
        totalCount
      }
      edges {
        cursor
        node {
          vendor
          vendors
          components
          licenses
          license
          type
          subtype
          analytic {
            vulnerability {
              critical
              high
              medium
              low
            }
            exploit {
              threatActor
              ransomware
              botnet
              inTheWild
              inKnownExploitedVulnerabilities
              inTheWild
              exploitCode
            }
          }
        }
      }
    }
  }
`;

export const PACKAGE_DEPENDENCIES = gql`
  query packageDependenciesById($args: packageDependenciesByIdInput!) {
    packageDependenciesById(args: $args) {
      dependencies {
        direct {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
            scope {
              value
              description
            }
          }
          badges
        }
        indirect {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
            scope {
              value
              description
            }
          }
          badges
        }
        dependents {
          component {
            id
            name
            subtype
            package {
              type
            }
            version {
              id
              isConcrete
            }
            scope {
              value
              description
            }
          }
          badges
        }
      }
      associatedFiles {
        component {
          id
          name
          digest {
            md5
            sha1
            sha256
            __typename
          }
          package {
            name
            type
          }
          file {
            path
          }
        }
        badges
      }
    }
  }
`;

export const LIST_AI_PROVIDERS = gql`
  query listAiProviders($args: ListAiProvidersInput) {
    listAiProviders(args: $args) {
      aiProviders
    }
  }
`;

export const GET_AI_MODEL_DATA = gql`
  query getAiModelData($args: GetAiModelDataInput!) {
    getAiModelData(args: $args) {
      aiModelData {
        version
        name
        author
        organization
        description
        license
        aiArchitecture
      }
    }
  }
`;

export const QL_REMOVE_IDENTIFICATION = gql`
  mutation removeDependencies($args: IdentificationInput!) {
    asset {
      removeDependencies(args: $args) {
        err
      }
    }
  }
`;

export const QL_ADD_DEPENDENCY = gql`
  mutation addDependency($args: AddDependencyInput!) {
    asset {
      addDependency(args: $args) {
        err
      }
    }
  }
`;

export const QL_MODIFY_DEPENDENCY = gql`
  mutation modifyDependency($args: ModifyDependencyInput!) {
    asset {
      modifyDependency(args: $args) {
        err
      }
    }
  }
`;
